import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLeaf, FiShield, FiUsers, FiLock, FiCalendar, FiMapPin, FiDownload } = FiIcons;

export default function Sustainability() {
  // Official Responsible Business Statement URL
  const sustainabilityPdf = "https://media-manager-c.questera.ai/greta-media/2f6847c5255962cb7ef6c818c843620d0179d6b0e810fa4ca1144f00d96707af10e5b2228b1b15861dc89ecefec6c49b/documents/YXBwbGljYXRpb24vcGRm/375bd9dee6f9c07eac389251bb2f0acb.pdf";

  const sections = [
    {
      title: "Environmental Responsibility",
      icon: FiLeaf,
      content: [
        "We operate a cloud-first, asset-light model with no warehouse or data center, keeping our energy use and physical footprint low.",
        "We direct end-of-life equipment to certified electronics recyclers and do not send business hardware to landfill.",
        "We shred and responsibly dispose of paper records, minimize printing, and default to digital documents and electronic delivery.",
        "Where we can influence product flow, we support manufacturer take-back, trade-in, and refurbishment programs that extend hardware life."
      ]
    },
    {
      title: "Responsible & Ethical Sourcing",
      icon: FiShield,
      content: [
        "We purchase only through manufacturer-authorized distributors and OEMs — never gray-market, auction, or unverified sources — protecting customers from counterfeit or tampered products.",
        "We screen for Section 889 prohibited equipment and confirm Trade Agreements Act (TAA) country-of-origin compliance before quoting to government buyers.",
        "We expect supply-chain partners to comply with applicable labor, human-rights, and anti-corruption laws, and we transact only with established, reputable IT manufacturers and distributors."
      ]
    },
    {
      title: "Equal Opportunity & Inclusion",
      icon: FiUsers,
      content: [
        "As a certified woman-owned and minority-owned business, inclusion is part of who we are.",
        "We are committed to equal opportunity and non-discrimination in hiring, contracting, and all business dealings, regardless of race, sex, age, disability, or other protected status."
      ]
    },
    {
      title: "Governance, Ethics & Data Protection",
      icon: FiLock,
      content: [
        "We conduct business honestly and in compliance with applicable laws and contract requirements.",
        "We protect customer, contract, and personal data under a documented information-security program, including access controls, encryption, and incident response."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-20 lg:px-8">
        <div className="mb-16">
          <div className="text-xs font-black uppercase tracking-[0.3em] text-orange-600 mb-4">Corporate Responsibility</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 leading-tight uppercase">
            Sustainability & Responsible <br />Business Statement
          </h1>
          
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <div className="flex flex-wrap gap-6 text-sm font-bold text-slate-400 uppercase tracking-widest border-y border-slate-100 py-6 flex-grow">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="h-4 w-4 text-orange-500" />
                Effective June 2026
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiMapPin} className="h-4 w-4 text-orange-500" />
                Huntington Beach, California
              </div>
            </div>
            
            <a 
              href={sustainabilityPdf} 
              target="_blank" 
              rel="noopener noreferrer" 
              download="MelonEdge_Responsible_Business_Statement.pdf"
              className="flex items-center gap-2 rounded-xl bg-orange-500 px-6 py-4 text-[10px] font-black text-white hover:bg-slate-900 transition-all uppercase tracking-widest shadow-lg shadow-orange-100"
            >
              <SafeIcon icon={FiDownload} className="h-4 w-4" />
              Download Responsible Business Statement
            </a>
          </div>

          <p className="mt-10 text-xl leading-relaxed text-slate-600 font-medium font-serif italic">
            MelonEdge LLC is a woman- and minority-owned IT hardware reseller serving government, education, and utility customers. As a small, asset-light business, we are committed to operating responsibly — minimizing our environmental footprint, sourcing ethically, and treating people fairly.
          </p>
        </div>

        <div className="space-y-16">
          {sections.map((section, idx) => (
            <section key={idx} className="relative pl-8 md:pl-0">
              <div className="hidden md:flex absolute -left-16 top-0 h-12 w-12 items-center justify-center rounded-xl bg-slate-50 text-slate-400">
                <SafeIcon icon={section.icon} className="h-6 w-6" />
              </div>
              
              <div className="md:border-l-2 md:border-slate-100 md:pl-12">
                <div className="flex items-center gap-3 mb-6 md:hidden">
                  <SafeIcon icon={section.icon} className="h-5 w-5 text-orange-500" />
                  <h2 className="text-xl font-black uppercase tracking-widest text-slate-900">{section.title}</h2>
                </div>
                <h2 className="hidden md:block text-xl font-black uppercase tracking-widest text-slate-900 mb-8">{section.title}</h2>
                
                <ul className="space-y-6">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex gap-4 group">
                      <div className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500 group-hover:scale-150 transition-transform" />
                      <p className="text-lg leading-relaxed text-slate-500 font-medium">
                        {item}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}